import { Fragment, lazy } from 'react';
import { Route } from 'react-router-dom';

import { toChapterReaderPaths } from '@/utils/lesson/chapterReaderPaths';
import { LEGACY_LESSON_PATHS } from '@/utils/lesson/legacyLessonPaths';
import { LESSON_TAB_PATHS } from '@/utils/lesson/lessonTabs';

import LessonTabIndexRedirect from './LessonTabIndexRedirect';

const InstructionsTab = lazy(
  () => import('@/pages/lesson/tabs/InstructionsTab'),
);
const ResourcesTab = lazy(() => import('@/pages/lesson/tabs/ResourcesTab'));

const loadMaterialTab = () => import('@/pages/lesson/tabs/MaterialTab');
const loadMaterialChapterRoute = () =>
  import('@/pages/lesson/MaterialChapterRoute');
const loadTeacherMaterialTab = () =>
  import('@/pages/lesson/tabs/TeacherMaterialTab');
const loadTeacherMaterialChapterRoute = () =>
  import('@/pages/lesson/TeacherMaterialChapterRoute');

const MaterialTab = lazy(loadMaterialTab);
const MaterialChapterRoute = lazy(loadMaterialChapterRoute);
const TeacherMaterialTab = lazy(loadTeacherMaterialTab);
const TeacherMaterialChapterRoute = lazy(loadTeacherMaterialChapterRoute);

export const preloadMaterialRoutes = () =>
  Promise.all([
    loadMaterialTab(),
    loadMaterialChapterRoute(),
    loadTeacherMaterialTab(),
    loadTeacherMaterialChapterRoute(),
  ]);
const ChallengesTab = lazy(() => import('@/pages/lesson/tabs/ChallengesTab'));

const ActivitiesTab = lazy(() => import('@/pages/lesson/tabs/ActivitiesTab'));

const ActivityPageSwitcher = lazy(
  () => import('@/pages/activity/ActivityPageSwitcher'),
);

const ActivitySuccessPage = lazy(
  () => import('@/pages/activity/ActivitySuccessPage'),
);

export function materialRoutes() {
  return (
    <Fragment>
      <Route path={LESSON_TAB_PATHS.material} element={<MaterialTab />} />
      {toChapterReaderPaths('material').map(path => (
        <Route key={path} path={path} element={<MaterialChapterRoute />} />
      ))}
      <Route
        path={LESSON_TAB_PATHS.teacherBook}
        element={<TeacherMaterialTab />}
      />
      {toChapterReaderPaths('teacherBook').map(path => (
        <Route
          key={path}
          path={path}
          element={<TeacherMaterialChapterRoute />}
        />
      ))}
    </Fragment>
  );
}

type LessonRoutesParams = {
  basePath: string;
  lessonElement: JSX.Element;
  legacyElement: JSX.Element;
};

export default function lessonRoutes({
  basePath,
  lessonElement,
  legacyElement,
}: LessonRoutesParams) {
  return (
    <Fragment key={basePath}>
      <Route path={basePath} element={lessonElement}>
        <Route index element={<LessonTabIndexRedirect />} />
        <Route
          path={LESSON_TAB_PATHS.instruction}
          element={<InstructionsTab />}
        />
        <Route path={LESSON_TAB_PATHS.resources} element={<ResourcesTab />} />
        {materialRoutes()}
        <Route path={LESSON_TAB_PATHS.challenge} element={<ChallengesTab />} />
        <Route path={LESSON_TAB_PATHS.activity} element={<ActivitiesTab />} />
        {LEGACY_LESSON_PATHS.map(path => (
          <Route key={path} path={path} element={legacyElement} />
        ))}
      </Route>
      <Route
        path={`${basePath}/${LESSON_TAB_PATHS.activity}/:activityId`}
        element={<ActivityPageSwitcher />}
      />
      <Route
        path={`${basePath}/${LESSON_TAB_PATHS.activity}/:activityId/success`}
        element={<ActivitySuccessPage />}
      />
    </Fragment>
  );
}
